import { Link } from 'react-router-dom';
import { EspacoDeTrabalho, nomeDoEspaco, rotaDoEspaco } from './modulos';
import { useSessao } from './sessao';

/**
 * As duas páginas de «não é por aqui» (onda 295, spec 03 §10 e §11).
 *
 * «Quem trabalha no laboratório não terá acesso ao módulo de campo e quem
 * trabalha em campo […] não terá acesso ao LabManager» (Humberto,
 * 06/10/2026). Desde a H1 o espaço vale de verdade: a API recusa com «Você
 * não tem acesso ao Hub.» (`RN-242`), e a tela tem de dizer a mesma coisa —
 * e não desenhar a tela pedida para ela quebrar em seguida com um 403 no
 * meio do formulário.
 *
 * As duas moram no lugar do conteúdo, DENTRO da moldura: a faixa, a
 * engrenagem e a barra da persona continuam. A pessoa recusada ainda sabe
 * quem ela é, ainda pode relatar o defeito, e o administrador que vestiu uma
 * persona ainda tem o «voltar a ser eu» à vista.
 */

/**
 * A tela pedida é de um espaço que a pessoa não alcança — o laboratorista
 * que abre um link do Hub recebido por e-mail, o gestor do laboratório que
 * abre o link antigo do Campo de uma OS.
 *
 * O botão leva ao espaço de VOLTA (`recusaDaTela`: o que a pessoa escolheu,
 * se ela o alcança; senão o primeiro que ela alcança) e leva o espaço junto
 * na navegação, para a moldura abrir nele mesmo quando o destino é uma tela
 * de mais de um espaço (a lista de OS é do Hub e do LabManager). Se as
 * permissões não dessem destino nenhum nesse espaço, não haveria botão: um
 * «Voltar» que leva a outra recusa é pior do que nenhum.
 */
export function RecusaDoEspaco({ espaco, voltar }: {
  espaco: EspacoDeTrabalho;
  voltar: EspacoDeTrabalho;
}) {
  const { eu } = useSessao();
  const nome = nomeDoEspaco(espaco);
  const destino = rotaDoEspaco(voltar, eu?.permissoes ?? []);
  return (
    <section className="sem-acesso" aria-labelledby="sem-acesso-titulo">
      <h1 id="sem-acesso-titulo">Você não tem acesso ao {nome}</h1>
      <p>
        Esta página é do {nome}, e a sua conta não tem acesso a ele. Se precisar,
        fale com o administrador.
      </p>
      {destino && (
        <Link className="botao" to={destino} state={{ espaco: voltar }}>
          Voltar ao {nomeDoEspaco(voltar)}
        </Link>
      )}
    </section>
  );
}

/**
 * A conta que não alcança espaço nenhum — a conta nova, sem tipo, ou a que
 * teve todas as permissões de espaço negadas por exceção.
 *
 * Sem botão de propósito: não há espaço para onde voltar, e a recusa de um
 * espaço com «Voltar» para lugar nenhum seria um beco (ponto de atenção 4 do
 * plano da H1). A engrenagem continua ao lado do nome — os Relatos abrem —, e
 * é por ela que a pessoa avisa que ficou sem acesso.
 */
export function ContaSemAcesso() {
  return (
    <section className="sem-acesso" aria-labelledby="sem-acesso-titulo">
      <h1 id="sem-acesso-titulo">Sua conta ainda não tem acesso</h1>
      <p>Fale com o administrador.</p>
    </section>
  );
}
